import type { ReactNode } from "react";
import type { CombinedContractListItem } from "@/features/hr/contracts/types";
import { PERMISSIONS } from "@/shared/consts";
import {
  CONTRACT_COMPLETED,
  CONTRACT_SIGNED,
  JOB_APP_APPROVED,
  JOB_APP_PENDING,
  JOB_APP_REVIEW,
  ORDER_ACKNOWLEDGED,
  ORDER_UPLOADED,
  SIGNING_STATUS_FULLY_SIGNED,
} from "@/features/hr/contracts/consts";
import { isDraftSigningStatus } from "@/shared/utils";
import { getIdFromApiUrl } from "./urlId";

export type ContractAction =
  | "download"
  | "submit_for_signing"
  | "submit_for_mobile_signing"
  | "preview"
  | "preview_job_application"
  | "review_job_application"
  | "review_job_application_revision"
  | "review_job_application_reject"
  | "preview_order"
  | "complete_hiring"
  | "download_contract"
  | "download_contract_pdf"
  | "download_contract_docx"
  | "edit_contract"
  | "delete_contract";

export interface ActionConfig {
  id: ContractAction;
  label: string;
  variant: "primary" | "secondary" | "danger" | "destructive" | "tertiary";
  icon?: ReactNode;
}

export interface ContractActions {
  actions: ActionConfig[];
  isLoading: boolean;
}

export function getAvailableActions(
  contract: CombinedContractListItem,
  isLoading: boolean,
  t: (key: string) => string,
): ContractActions {
  const { candidate_stage, signing_status, is_legacy_import, legacy_contract_pdf_url } = contract;

  const primaryActions: ActionConfig[] = [];
  const secondaryActions: ActionConfig[] = [];

  const canSubmitForSigning = isDraftSigningStatus(signing_status) && !is_legacy_import;

  const isContractDraft = isDraftSigningStatus(signing_status) && !is_legacy_import;

  if (canSubmitForSigning) {
    primaryActions.push({
      id: "submit_for_signing",
      label: t("actions.submitForSigning"),
      variant: "primary",
    });
  }

  if (isContractDraft) {
    secondaryActions.push({
      id: "edit_contract",
      label: t("actions.editContract"),
      variant: "secondary",
    });
  }

  const isContractFullySigned = signing_status === SIGNING_STATUS_FULLY_SIGNED;
  const canDownloadContract =
    isContractFullySigned ||
    (is_legacy_import && !!legacy_contract_pdf_url) ||
    candidate_stage === CONTRACT_SIGNED ||
    candidate_stage === JOB_APP_PENDING ||
    candidate_stage === JOB_APP_REVIEW ||
    candidate_stage === JOB_APP_APPROVED ||
    candidate_stage === CONTRACT_COMPLETED;

  const hasSigningDocument = contract.signing_document !== null;

  if (canDownloadContract) {
    if (hasSigningDocument) {
      secondaryActions.push({
        id: "download_contract",
        label: t("contract.download"),
        variant: "secondary",
      });
    } else {
      secondaryActions.push({
        id: "download_contract_pdf",
        label: t("contract.downloadPdf"),
        variant: "secondary",
      });
    }
  }

  if (!isContractFullySigned && !canDownloadContract && !is_legacy_import) {
    secondaryActions.push({
      id: "download_contract_docx",
      label: t("actions.downloadDraft"),
      variant: "secondary",
    });
  }

  const orderId = getIdFromApiUrl(contract.order_url);
  if (orderId) {
    if (candidate_stage === ORDER_UPLOADED || candidate_stage === ORDER_ACKNOWLEDGED) {
      primaryActions.push({
        id: "complete_hiring",
        label: t("order.actions.completeHiring"),
        variant: "primary",
      });
    }
  }

  if (isContractDraft) {
    secondaryActions.push({
      id: "delete_contract",
      label: t("actions.deleteContract"),
      variant: "danger",
    });
  }

  const actions = [...primaryActions, ...secondaryActions];

  return {
    actions,
    isLoading,
  };
}

export function filterActionsByPermissions(
  actions: ActionConfig[],
  hasPermission: (code: string) => boolean,
): ActionConfig[] {
  return actions.filter((action) => {
    if (action.id === "submit_for_signing" || action.id === "submit_for_mobile_signing") {
      return hasPermission(PERMISSIONS.HR.SIGN_CONTRACT);
    }

    if (
      action.id === "review_job_application" ||
      action.id === "review_job_application_revision" ||
      action.id === "review_job_application_reject"
    ) {
      return hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
    }

    if (action.id === "complete_hiring") {
      return hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);
    }

    if (action.id === "edit_contract") {
      return hasPermission(PERMISSIONS.HR.EDIT_CONTRACT);
    }

    if (action.id === "delete_contract") {
      return hasPermission(PERMISSIONS.HR.CREATE_CONTRACT);
    }

    return true;
  });
}
